import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AuthorizationAwareComponent } from '../../authorization-aware-component';
import { AppState } from '../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { AddToCollectionWizard } from '../../../collections/add-to-collection-wizard/add-to-collection-wizard';
import { CollectionService } from '../../federation/collection/collection.service';
import { ToastService } from '../../toast/toast.service';
import { WizardRunnerService } from '../../wizard/wizard-runner.service';
import { CreateOrEditCollectionWizard } from '../../../collections/create-or-edit-collection-wizard/create-or-edit-collection-wizard';
import { StoreExportWizard } from '../export/store-export-wizard/store-export-wizard';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { Pageable } from '../../service/pageable';
import { ClipboardUtil } from '../../util/clipboard-util';
import { Collection } from '../../federation/collection/collection';
import { HttpParams } from '@angular/common/http';
import { HitTrackingService } from '../../hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from '../../constants/user-tracking.constants';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';
import { DetailState, selectDetailState } from 'src/app/detail/detail-state';

@Component({
  standalone: false,
  selector: 'mtn-store-actions-menu-button',
  templateUrl: './store-actions-menu-button.component.html',
  styleUrls: ['./store-actions-menu-button.component.scss']
})
export class StoreActionsMenuButtonComponent extends AuthorizationAwareComponent {

  @Input()
  storeUuids: string[];

  @Input()
  assetReviewDisabled: boolean = false;

  @Input()
  assetReviewTooltip: string = '';

  @Input()
  assetReviewDropdown: boolean;

  collections: Collection[] = [];
  @Output() assetReviewOpened = new EventEmitter<boolean>();

  openAssetReview(){
    if (!this.assetReviewDisabled) {
      this.assetReviewOpened.emit(true);
    }
  }

  isStoreDetailPage: boolean = false;
  store: any;
  marketName: any;
  constructor(private collectionService: CollectionService,
    protected ngrxStore: NgrxStore<AppState>,
    private toaster: ToastService,
    private userProfileService: UserProfileService,
    private hitTrackingService: HitTrackingService,
    private activatedRoute: ActivatedRoute,
    private wizardRunner: WizardRunnerService) {
    super(ngrxStore);
    this.subscribeToDetailState();

  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state: DetailState) => {
          this.store = state.store;
          const feature = state.marketArea?.getFeature();
          this.marketName = feature?.properties ? feature.properties['name'] : null;
        })
    );
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
    this.loadCollections();
    this.activatedRoute.params
      .pipe(take(1))
      .subscribe((params: any) => {
        params.uuid ? this.isStoreDetailPage = true : this.isStoreDetailPage = false
      });
  }

  addToCollection(collection: Collection): void {
    this.collectionService.addAllIncludedFeatures(collection.uuid, this.storeUuids)
      .subscribe(() => {
        this.toaster.info('Successfully updated collection');
        const trackingMetadata = {
          actionType: 'Add to Collection',
          numberOfProperties: this.storeUuids.length,
          properties: this.storeUuids.map(uuid => ({ uuid })) // If you have more details per property, add here
        };

        const location = this.store?.space?.location;
        const shoppingCenter = this.store?.space?.shoppingCenter;
        const metadata = {
          storeId: this.store.uuid,
          storeName: this.store.name,
          addressLine1: location?.addressLine1,
          city: location?.city,
          cbsa: this.marketName,
          state: location?.state,
          postalCode: location?.postalCode,
          county: location?.county,
          shoppingCenterName: shoppingCenter?.name,
          landlord: shoppingCenter?.ownerCompany?.name
        }


        // ✅ Call tracking API
        this.hitTrackingService.hitTheUserTracking(
          this.isStoreDetailPage ? USER_TRACKING_SOURCES.STORE_DETAIL_EXPORT_WIZARD : USER_TRACKING_SOURCES.STORE_EXPORT_WIZARD,                       // hit_source
          this.isStoreDetailPage ? metadata : trackingMetadata,                        // returnedData
          USER_TRACKING_ACTIONS.UPDATE,            // action
          USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.UPDATE]  // action_description
        ).subscribe();
      });
  }

  copyUrlToClipboard(): void {
    const storeUuid = this.storeUuids[0];
    const detailsPageUrl = `${window.location.origin}/detail/store/${storeUuid}`;

    ClipboardUtil.copyValueToClipboard(detailsPageUrl);
    this.toaster.info("Copied Store Details URL to clipboard");

    const location = this.store?.space?.location;
    const shoppingCenter = this.store?.space?.shoppingCenter;
    if (this.isStoreDetailPage) {
      const metadata = {
        storeId: this.store.uuid,
        storeName: this.store.name,
        addressLine1: location?.addressLine1,
        city: location?.city,
        cbsa: this.marketName,
        state: location?.state,
        postalCode: location?.postalCode,
        county: location?.county,
        shoppingCenterName: shoppingCenter?.name,
        landlord: shoppingCenter?.ownerCompany?.name
      }
      this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.STORE_DETAIL_ACTIONS_MENU,
        metadata,
        USER_TRACKING_ACTIONS.SHARE_STORE_URL,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SHARE_STORE_URL](storeUuid)
      ).subscribe();
    }
    else {

      // ✅ Hit Tracking
      this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.STORE_ACTIONS_MENU,
        {
          actionType: 'share',
          storeUuid
        },
        USER_TRACKING_ACTIONS.SHARE_STORE_URL,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SHARE_STORE_URL](storeUuid)
      ).subscribe();

    }
  }


  openAddToCollectionWizard(): void {
    const wizard = new AddToCollectionWizard();
    wizard.model = {
      storeUuids: this.storeUuids,
      userProfileUuid: this.currentUser.uuid
    };

    this.wizardRunner.run(wizard);
  }

  openCreateNewCollectionWizard(): void {
    const wizard = new CreateOrEditCollectionWizard();
    wizard.model = {
      companyUuid: this.currentUser.company.uuid,
      storeUuids: this.storeUuids,
      userProfileUuid: this.currentUser.uuid
    };

    this.wizardRunner.run(wizard);
  }

  openDownloadWizard(): void {
    const wizard = new StoreExportWizard();
    wizard.model = {
      uuids: this.storeUuids
    };

    this.wizardRunner.run(wizard);
  }

  /**
   * We only load the most recent 5 favorited collections.
   *
   * TODO We want to track the user's activity with each collection, not just rely on createdDate or updatedDate.
   * We want to know which were the last five favorited collections the user actually interacted with. See MTN-1742.
   */
  loadCollections(): void {
    const params = new HttpParams()
      .set('size', '5')
      .set('sort', 'createdDate,desc');

    this.userProfileService.findOnesFavoriteCollections(this.currentUser.uuid, params, this.currentUser.company?.uuid)
      .subscribe((result: Pageable<Collection>) => {
        if (result?.totalElements) {
          this.collections = result.content;
        }
      });
  }

}
